<template>
  <div class="relative">
    <aha-info-box class="mb-4">
      <p>Changing settings here takes effect on all quiz slides.</p>
    </aha-info-box>

    <!-- Allow lobby chatting -->
    <div class="flex justify-between items-center mb-5">
      <div class="flex items-center flex-grow group">
        <aha-icon
          name="comment"
          class="text-base-70"
          width="20"
          height="20"
        ></aha-icon>
        <span class="px-2 font-normal">Live chat</span>
        <b-button
          v-tooltip="chatSettingTooltip"
          class="text-secondary-40 cursor-pointer hidden group-hover:inline-block"
        >
          <i class="fas fa-question-circle" />
        </b-button>
      </div>
      <toggle-button
        v-model="presentation.isEnableChat"
        :disabled="isDisableEditPresentation"
        :width="35"
        :height="20"
        class="mb-0"
        :color="{
          checked: '#348EF6',
          unchecked: '#D3D7E1'
        }"
        @change="onChangeAllowLobbyChat()"
      />
    </div>

    <!-- Enable default background music -->
    <div class="flex justify-between items-center mb-5">
      <div class="flex items-center flex-grow group">
        <aha-icon
          name="music"
          class="text-base-70"
          width="20"
          height="20"
        ></aha-icon>
        <span class="px-2 font-normal">Lobby music</span>
        <b-button
          v-tooltip="enableQuizMusicTooltip"
          class="text-secondary-40 cursor-pointer hidden group-hover:inline-block"
        >
          <i class="fas fa-question-circle" />
        </b-button>
      </div>
      <toggle-button
        v-model="presentation.isEnableQuizMusic"
        :disabled="isDisableEditPresentation"
        :width="35"
        :height="20"
        class="mb-0"
        :color="{
          checked: '#348EF6',
          unchecked: '#D3D7E1'
        }"
        @change="onChangeQuizMusic()"
      />
    </div>

    <!-- Enable 5 second countdown before participants can answer -->
    <div class="flex justify-between items-center mb-5">
      <div class="flex items-center flex-grow group">
        <aha-icon
          name="hourglass"
          class="text-base-70"
          width="20"
          height="20"
        ></aha-icon>
        <span class="px-2 font-normal">5s countdown</span>
        <b-button
          v-tooltip="enable5SecondsCountDownTooltip"
          class="text-secondary-40 cursor-pointer hidden group-hover:inline-block"
        >
          <i class="fas fa-question-circle" />
        </b-button>
      </div>
      <toggle-button
        v-model="presentation.enableQuizCoundown"
        :disabled="isDisableEditPresentation"
        :width="35"
        :height="20"
        class="mb-0"
        :color="{
          checked: '#348EF6',
          unchecked: '#D3D7E1'
        }"
        @change="onChangeQuizCountdown()"
      />
    </div>

    <div class="flex justify-between items-center mb-5">
      <div class="flex items-center flex-grow group">
        <aha-icon
          name="people-group"
          class="text-base-70"
          width="20"
          height="20"
        ></aha-icon>
        <span class="px-2 font-normal">Play as teams</span>
        <b-button
          v-tooltip="teamPlayTooltip"
          class="text-secondary-40 cursor-pointer hidden group-hover:inline-block"
        >
          <i class="fas fa-question-circle" />
        </b-button>
        <span
          v-if="presentation.teamPlay"
          class="ml-auto text-sm font-bold cursor-pointer px-2 text-primary-90"
          @click="toggleTeamSetUp"
        >Set up</span>
      </div>
      <toggle-button
        v-model="presentation.teamPlay"
        :disabled="isDisableEditPresentation"
        :width="35"
        :height="20"
        class="mb-0"
        :color="{
          checked: '#348EF6',
          unchecked: '#D3D7E1'
        }"
        @change="onChangeTeamPlay"
      />
    </div>

    <div class="flex justify-between items-center mb-4">
      <div class="flex items-center flex-grow group">
        <aha-icon
          name="shuffle"
          class="text-base-70"
          width="20"
          height="20"
        ></aha-icon>
        <span class="px-2 font-normal">Shuffle options</span>
        <b-button
          v-tooltip="slideOptionsShufflingTooltip"
          class="text-secondary-40 cursor-pointer hidden group-hover:inline-block"
        >
          <i class="fas fa-question-circle" />
        </b-button>
      </div>
      <toggle-button
        v-model="presentation.areSlideOptionsShuffling"
        :disabled="isDisableEditPresentation"
        :width="35"
        :height="20"
        class="mb-0"
        :color="{
          checked: '#348EF6',
          unchecked: '#D3D7E1'
        }"
        @change="onChangeSlideOptionsShuffling()"
      />
    </div>

    <div
      class="flex justify-between items-center mb-5"
      :class="{
        'opacity-50 cursor-not-allowed': isDisabledRevealCorrectAnswers
      }"
    >
      <div class="flex items-center flex-grow group">
        <aha-icon
          name="hand-pointer"
          class="text-base-70"
          width="20"
          height="20"
        ></aha-icon>
        <span class="px-2 font-normal">Manually show correct answers</span>
        <b-button
          v-tooltip="manualRevealCorrectAnswersTooltip"
          class="text-secondary-40 cursor-pointer hidden group-hover:inline-block"
        >
          <i class="fas fa-question-circle" />
        </b-button>
      </div>
      <toggle-button
        v-model="presentation.manualRevealCorrectAnswers"
        :disabled="isDisabledRevealCorrectAnswers || isDisableEditPresentation"
        :width="35"
        :height="20"
        class="mb-0"
        :color="{
          checked: '#348EF6',
          unchecked: '#D3D7E1'
        }"
        @change="onChangeManualRevealCorrectAnswers()"
      />
    </div>

    <div v-if="isDisableEditPresentation" class="overlay-disable-setting"></div>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import utils from '@/util'
import gaCategories from '@/constant/gaCategories'
import EventBus from '@/eventBus'

export default {
  computed: {
    ...mapState({
      presentation: (state) => state.presentation,
      currentUser: (state) => state.currentUser,
      isDisableEditPresentation: (state) => state.isDisableEditPresentation,
      isDisabledRevealCorrectAnswers: (state) => {
        return state.presentation && state.presentation.audiencePacing
      },
      isEnableTeamPlay: (state) => {
        return state.presentation && state.presentation.teamPlay
      },
    }),

    chatSettingTooltip() {
      return {
        content: 'Check this to allow participants to send public live chat messages on the following screens:<br/> - Quiz lobby screen <br/> - QR code slides <br/> - Leaderboard slides',
      }
    },
    enable5SecondsCountDownTooltip() {
      return 'Check this to give all participants a short time to read the question'
    },
    enableQuizMusicTooltip() {
      return 'Check this box to play default background music on the lobby screen and all leaderboard slides during a quiz'
    },
    teamPlayTooltip() {
      return 'Let the participants play quiz and be ranked as teams instead of being ranked individually'
    },
    slideOptionsShufflingTooltip() {
      return 'Randomly rearrange the options to make it harder for participants to share answers'
    },
    manualRevealCorrectAnswersTooltip() {
      if (this.presentation.audiencePacing) {
        return 'This setting is not available for self-paced quizzes'
      }
      return 'Manually show audience answers at the end of a question'
    },
  },
  methods: {
    onChangeAllowLobbyChat() {
      const { isEnableChat } = this.presentation
      this.$emit('updatePresentation', {
        isEnableChat,
      })
      const gaText = isEnableChat ? 'Enable' : 'Disable'
      utils.gaTrackEvent(
        gaCategories.PRESENTATION_EDITOR,
        'Settings menu > Quiz settings > Chat In Lobby > Enable chat in the lobby',
        `${gaText}`,
      )
    },

    onChangeQuizCountdown() {
      this.$emit('updatePresentation', {
        enableQuizCoundown: this.presentation.enableQuizCoundown,
      })
      if (this.presentation.enableQuizCoundown) {
        utils.gaTrackEvent(
          gaCategories.PRESENTATION_EDITOR,
          'Settings menu > Quiz settings > Enable 5 second coundown > Enable',
        )
      } else {
        utils.gaTrackEvent(
          gaCategories.PRESENTATION_EDITOR,
          'Settings menu > Quiz settings > Enable 5 second coundown > Disable',
        )
      }
    },

    onChangeQuizMusic() {
      this.$emit('updatePresentation', {
        isEnableQuizMusic: this.presentation.isEnableQuizMusic,
      })
      if (this.$store.state.presentation.isEnableQuizMusic) {
        utils.gaTrackEvent(
          gaCategories.PRESENTATION_EDITOR,
          'Settings menu > Quiz settings > Enable Quiz Music',
        )
      } else {
        utils.gaTrackEvent(
          gaCategories.PRESENTATION_EDITOR,
          'Settings menu > Quiz settings > Disable Quiz Music',
        )
      }
    },

    onChangeTeamPlay() {
      const payload = {
        teamPlay: this.presentation.teamPlay,
      }
      if (this.presentation.teamPlay === false) {
        this.presentation.isHideIndividualLeaderboard = false
        payload.isHideIndividualLeaderboard = this.presentation.isHideIndividualLeaderboard
      }
      if (
        !this.presentation.teamData ||
        (this.presentation.teamData &&
          !this.presentation.teamData.length)
      ) {
        this.presentation.teamData = []
        if (
          this.presentation.teamData.length !==
          this.presentation.teamCount
        ) {
          for (let i = 1; i <= this.presentation.teamCount; i++) {
            this.presentation.teamData.push({
              id: i,
              name: `Team ${i}`,
              visible: true,
            })
          }
        }
        payload.teamData = this.presentation.teamData
      }
      this.$emit('updatePresentation', payload)

      utils.gaTrackEvent(
        gaCategories.PRESENTATION_EDITOR,
        'Settings menu > Quiz settings > Play as team checkbox',
        payload.teamPlay,
      )
    },

    toggleTeamSetUp() {
      this.$emit('showTeamSetUp')
      this.$emit('hideMainHeader')
      EventBus.$emit('show-setting-popup', { defaultExpandedAccordion: 'quiz' })
    },

    onChangeSlideOptionsShuffling() {
      this.$emit('updatePresentation', {
        areSlideOptionsShuffling: this.presentation.areSlideOptionsShuffling,
      })
      if (this.presentation.areSlideOptionsShuffling) {
        utils.gaTrackEvent(
          gaCategories.PRESENTATION_EDITOR,
          'Settings menu > Quiz settings > Enable shuffle the options',
        )
      } else {
        utils.gaTrackEvent(
          gaCategories.PRESENTATION_EDITOR,
          'Settings menu > Quiz settings > Disable shuffle the options',
        )
      }
    },

    onChangeManualRevealCorrectAnswers() {
      this.$emit('updatePresentation', {
        manualRevealCorrectAnswers: this.presentation.manualRevealCorrectAnswers,
      })
      if (this.presentation.manualRevealCorrectAnswers) {
        utils.gaTrackEvent(
          gaCategories.PRESENTATION_EDITOR,
          'Settings menu > Quiz settings > Enable manual reveal correct answers',
        )
      } else {
        utils.gaTrackEvent(
          gaCategories.PRESENTATION_EDITOR,
          'Settings menu > Quiz settings > Disable manual reveal correct answers',
        )
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.overlay-disable-setting {
  @apply bg-base-0 opacity-50 absolute left-0 top-0 w-full h-full;
}
</style>
